import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FitText } from '../FitText';
import type { NotableSegment, VoiceoverNotableSegmentClip } from '../../types/videoEdit';
import { EditorVideo } from './EditorVideo';
import { EBTN_TRANSPORT } from './editorButtonStyles';
import { fitAspect, previewStage, type PhoneViewport } from './phoneLayout';
import { aspectFor, clipLength, previewClipTitle, rememberAspect } from './lessonPreview';

/**
 * The lesson preview on a phone in portrait (spec 15.30): the desktop
 * preview's elements in its order, laid out for the phone width. The header
 * ("Lesson Preview", "Clip N of M · title", Close) and the Previous / dots /
 * Next row are the shared ones in LessonVideoEditor; between them this
 * renders the clip at full width at its own aspect with our transport
 * (play, scrubber, time and length), then every clip as a 44 row with the
 * current one highlighted.
 *
 * One <video> element for the whole preview: the source changes between
 * clips but the element stays, so iOS lets the next clip carry on playing
 * after the coach's first tap. The play icon follows the element's own
 * events. A tap on the picture plays or pauses (EditorVideo).
 *
 * Landscape (spec 13.16): the same element, full size on black, with the
 * controls floating over it (close; one bottom pill with the clip's title,
 * Previous and Next; a centre play while paused). A quick tap shows or hides
 * them and never changes playback; they hide 3s into playback.
 */
export function PhoneLessonPreviewBody({
  clips, allClips, segments, index, onIndex, url, viewport, onClose,
}: {
  clips: VoiceoverNotableSegmentClip[];
  allClips: VoiceoverNotableSegmentClip[];
  segments: NotableSegment[];
  index: number;
  onIndex: (i: number) => void;
  url: string | null;
  viewport: PhoneViewport;
  /** Landscape: the close control floats over the video. */
  onClose: () => void;
}) {
  const landscape = viewport.landscape;
  const clip = clips[index];
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);
  const [shown, setShown] = useState(false);
  /* Whether the coach wants it playing: true on open, set by play and by a
     pause the coach made, but not by the pause a source change or a clip's
     natural end fires. A new clip starts playing when this is true. */
  const wantPlayRef = useRef(true);
  const switchingRef = useRef(false);
  const continueRef = useRef(false);
  useLayoutEffect(() => {
    switchingRef.current = true;
    continueRef.current = wantPlayRef.current;
  }, [url]);
  /* Bumped when the playing clip reports its shape (onLoadedMetadata), as
     the desktop preview does; nothing is read ahead. */
  const [, setAspectTick] = useState(0);

  const aspect = clip ? aspectFor(clip.id) : 16 / 9;
  const stage = landscape ? fitAspect(aspect, viewport.w, viewport.h) : previewStage(viewport, clips.length > 1, aspect);

  /* Landscape controls (spec 13.16): shown on open; a tap toggles them; they
     hide 3s after playback starts or after the last touch on them, and stay
     while paused. */
  const [chromeShown, setChromeShown] = useState(true);
  const chromeVisible = chromeShown || !playing;
  const playingRef = useRef(playing);
  useEffect(() => { playingRef.current = playing; });
  const chromeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armChromeHide = useCallback(() => {
    if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current);
    chromeTimerRef.current = setTimeout(() => { if (playingRef.current) setChromeShown(false); }, 3000);
  }, []);
  const pokeChrome = useCallback(() => { setChromeShown(true); armChromeHide(); }, [armChromeHide]);
  useEffect(() => { if (landscape && playing) armChromeHide(); }, [landscape, playing, armChromeHide]);
  useEffect(() => () => { if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current); }, []);
  const tapRef = useRef<{ t: number; x: number; y: number; ok: boolean } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    if (!landscape) return;
    if (e.touches.length !== 1 || (e.target as Element).closest('button')) { if (tapRef.current) tapRef.current.ok = false; return; }
    const t = e.touches[0];
    tapRef.current = { t: performance.now(), x: t.clientX, y: t.clientY, ok: true };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const a = tapRef.current;
    const t = e.touches[0];
    if (a && (e.touches.length !== 1 || !t || Math.hypot(t.clientX - a.x, t.clientY - a.y) > 10)) a.ok = false;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const a = tapRef.current;
    if (!a || e.touches.length > 0) return;
    tapRef.current = null;
    if (!a.ok || performance.now() - a.t >= 250) return;
    if (!playingRef.current) { pokeChrome(); return; }
    setChromeShown((v) => { if (!v) armChromeHide(); return !v; });
  };
  const duration = mediaDuration > 0 ? mediaDuration : clip?.videoDuration ?? 0;

  /* A new clip starts from 0, hidden until it has a frame. */
  const [shownFor, setShownFor] = useState(url);
  if (shownFor !== url) {
    setShownFor(url);
    setShown(false);
    setTime(0);
    setMediaDuration(0);
  }

  /* The time follows the video every frame while it plays. */
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => { const v = videoRef.current; if (v) setTime(v.currentTime); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) v.play().catch(() => setPlaying(false));
    else v.pause();
  }, []);

  if (!clip) return null;
  const title = previewClipTitle(clip, allClips, segments);

  return (
    /* One root in both orientations, so the video element below is never
       remounted when the phone turns. Portrait lays its children out in the
       sheet's own column (display: contents). */
    <div
      className={landscape ? 'relative flex-1 min-h-0 bg-black overflow-hidden' : 'contents'}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className={landscape ? 'bg-black absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : 'bg-black flex-none self-center relative'}
        style={{ width: stage.w, height: stage.h }}
      >
        {url ? (
          <EditorVideo
            ref={videoRef}
            src={url}
            playsInline
            className="w-full h-full object-contain"
            style={{ opacity: shown ? 1 : 0 }}
            tapToPlay={!landscape}
            wrapperClassName="w-full h-full"
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              if (v.videoWidth && v.videoHeight) { rememberAspect(clip.id, v.videoWidth / v.videoHeight); setAspectTick((n) => n + 1); }
              if (Number.isFinite(v.duration)) setMediaDuration(v.duration);
            }}
            onLoadedData={() => {
              setShown(true);
              switchingRef.current = false;
              if (continueRef.current) { continueRef.current = false; videoRef.current?.play().catch(() => setPlaying(false)); }
            }}
            onPlay={() => { wantPlayRef.current = true; setPlaying(true); }}
            onPlaying={() => setPlaying(true)}
            onPause={(e) => {
              setPlaying(false);
              if (!switchingRef.current && !e.currentTarget.ended) wantPlayRef.current = false;
            }}
            onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
            onEnded={() => {
              setPlaying(false);
              /* The next clip carries on; the last stays on its final frame. */
              if (index < clips.length - 1) onIndex(index + 1);
              else wantPlayRef.current = false;
            }}
          />
        ) : null}
        {(!url || !shown) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-db-oasis)]" />
          </div>
        )}
      </div>

      {landscape ? (
        /* Landscape (spec 13.16): the controls float over the video, inside
           the one inset rule (13.13). */
        <div className={`lve-controls z-[5] ${chromeVisible ? '' : 'lve-controls--hidden'}`} style={{ opacity: chromeVisible ? 1 : 0, transition: 'opacity 200ms ease' }} onPointerDown={pokeChrome}>
          <button type="button" onClick={onClose} className="lve-glass lve-pill lve-hit absolute left-0 top-0 w-11 flex items-center justify-center ios-press" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          {!playing && shown && (
            <button type="button" onClick={togglePlay} className="lve-glass lve-hit absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center ios-press" aria-label="Play">
              <svg className="w-8 h-8 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            </button>
          )}
          {/* Bottom pill: Previous, the clip's title (Subhead 15/20), Next. */}
          <div className="lve-glass lve-pill lve-hit absolute left-1/2 -translate-x-1/2 bottom-0 w-full max-w-[640px] flex items-center gap-2 px-1">
            <button type="button" onClick={() => onIndex(Math.max(0, index - 1))} disabled={index === 0} className="flex-none h-11 px-3 flex items-center gap-1 text-[15px] leading-[20px] ios-press disabled:opacity-40" aria-label="Previous clip">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              Previous
            </button>
            <FitText lines={2} className="flex-1 text-center text-[15px] leading-[20px] font-semibold">{title}</FitText>
            <button type="button" onClick={() => onIndex(Math.min(clips.length - 1, index + 1))} disabled={index === clips.length - 1} className="flex-none h-11 px-3 flex items-center gap-1 text-[15px] leading-[20px] ios-press disabled:opacity-40" aria-label="Next clip">
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* Transport: play, our scrubber (never under half the row), time and
          length in mono Subhead 15/20 (spec 15). */}
      <div className="flex items-center gap-2 h-11 flex-none px-3">
        <button type="button" onClick={togglePlay} className={`${EBTN_TRANSPORT} flex-none`} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? (
            <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
          ) : (
            <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          )}
        </button>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.05}
          value={Math.min(time, duration || 0)}
          onChange={(e) => { const v = videoRef.current; const t = parseFloat(e.target.value); setTime(t); if (v) v.currentTime = t; }}
          aria-label="Scrub"
          className="flex-1 min-w-[50%] h-11 lve-phone-range"
        />
        <span className="flex-none w-max font-mono text-[15px] leading-[20px] text-[var(--editor-text)] tabular-nums">
          {clipLength(time) || '0:00'} / {clipLength(duration) || '0:00'}
        </span>
      </div>

      {/* Every clip: 44 rows, Subhead 15/20, the current one highlighted. */}
      <div className="flex-1 min-h-0 overflow-y-auto" style={{ borderTop: '1px solid var(--editor-border)' }}>
        {clips.map((c, i) => {
          const current = i === index;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => { if (!current) onIndex(i); }}
              aria-current={current ? 'true' : undefined}
              className="w-full min-h-11 py-1 flex items-center gap-3 px-4 text-left ios-press"
              style={{ background: current ? 'var(--editor-surface)' : 'transparent', borderBottom: '1px solid var(--editor-border)' }}
            >
              <FitText lines={2} className={`flex-1 text-[15px] leading-[20px] ${current ? 'font-semibold text-[var(--color-db-oasis)]' : 'text-[var(--editor-text)]'}`}>
                {previewClipTitle(c, allClips, segments)}
              </FitText>
              <span className="flex-none font-mono text-[15px] leading-[20px] tabular-nums text-[var(--editor-text-muted)]">{clipLength(c.videoDuration)}</span>
            </button>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}
